import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { Link, createFileRoute } from '@tanstack/react-router'
import { LayoutGrid, Plus, Rows3, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SortState } from '@/components/shared/sortable-table-head'

import PageHeader from '@/components/layout/page-header'
import { ListPagination } from '@/components/shared/list-pagination'
import {
  SortableTableHead,
  sortItems,
  toggleSort,
} from '@/components/shared/sortable-table-head'
import DriverCard from '@/features/drivers/components/driver-card'
import { DriverTableRow } from '@/features/drivers/components/driver-table-row'
import { useGetDriversPaginated } from '@/features/drivers/hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/drivers/')({
  head: () => ({ meta: [{ title: localizedPageTitle('drivers') }] }),
  component: DriversPage,
})

const driverAccessor = (item: any, key: string) => {
  switch (key) {
    case 'name':
      return `${item.firstName} ${item.lastName}`
    case 'phone':
      return item.phone
    case 'license':
      return item.licenseNumber
    case 'status':
      return item.status
    case 'vehicle':
      return item.vehicle?.licensePlate
    default:
      return null
  }
}

type LayoutView = 'cards' | 'table'

function DriversPage() {
  useDocumentTitle('drivers')
  const { t } = useTranslation()

  const [layout, setLayout] = useState<LayoutView>(() => {
    return (localStorage.getItem('drivers-layout') ?? 'cards') as LayoutView
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<SortState>(null)

  const { data, isLoading } = useGetDriversPaginated({
    page,
    pageSize,
    search: debouncedSearch || undefined,
  })

  const handleLayoutChange = (newLayout: LayoutView) => {
    setLayout(newLayout)
    localStorage.setItem('drivers-layout', newLayout)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  const handleSort = (key: string) => setSort((prev) => toggleSort(prev, key))

  const sortedDrivers = useMemo(
    () => sortItems(data?.items ?? [], sort, driverAccessor),
    [data?.items, sort],
  )

  return (
    <div className="flex-1 space-y-4">
      <PageHeader
        title={t('drivers:title')}
        subtitle={t('drivers:subtitle')}
        hideBackButton
      />

      <Card className="bg-background max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
        <CardHeader className="max-md:px-0">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center justify-between gap-2 md:block">
              <div>
                <CardTitle>{t('drivers:allDrivers')}</CardTitle>
                <CardDescription>
                  {data?.totalCount ?? 0} {t('drivers:totalDrivers')}
                </CardDescription>
              </div>
              <div className="md:hidden">
                <Tabs
                  value={layout}
                  onValueChange={(v) => handleLayoutChange(v as LayoutView)}
                >
                  <TabsList variant="icon">
                    <TabsTrigger
                      value="cards"
                      aria-label={t('common:layout.cards')}
                      variant="icon"
                    >
                      <LayoutGrid className="size-4" />
                    </TabsTrigger>
                    <TabsTrigger
                      value="table"
                      aria-label={t('common:layout.table')}
                      variant="icon"
                    >
                      <Rows3 className="size-4" />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center md:gap-2">
              <div className="hidden md:block">
                <Tabs
                  value={layout}
                  onValueChange={(v) => handleLayoutChange(v as LayoutView)}
                >
                  <TabsList variant="icon">
                    <TabsTrigger
                      value="cards"
                      aria-label={t('common:layout.cards')}
                      variant="icon"
                    >
                      <LayoutGrid className="size-4" />
                    </TabsTrigger>
                    <TabsTrigger
                      value="table"
                      aria-label={t('common:layout.table')}
                      variant="icon"
                    >
                      <Rows3 className="size-4" />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="relative w-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder={t('drivers:searchDrivers')}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-11 pl-9"
                />
              </div>

              <Link to="/dashboard/drivers/create" className="w-full sm:w-auto">
                <Button
                  className="primary-bg-gradient w-full whitespace-nowrap sm:w-auto"
                  size="lg"
                >
                  <Plus className="size-4" />
                  {t('drivers:addDriver')}
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 max-md:px-0">
          {isLoading ? (
            layout === 'cards' ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-72 w-full rounded-md" />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <Skeleton className="h-10 w-full rounded-none" />
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-16 w-full rounded-none border-t"
                  />
                ))}
              </div>
            )
          ) : data?.items && data.items.length > 0 ? (
            <>
              {layout === 'cards' ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {sortedDrivers.map((driver) => (
                    <DriverCard key={driver.id} driver={driver as any} />
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <SortableTableHead
                          sortKey="name"
                          sort={sort}
                          onSort={handleSort}
                        >
                          {t('drivers:table.driver')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="phone"
                          sort={sort}
                          onSort={handleSort}
                        >
                          {t('drivers:table.phone')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="license"
                          sort={sort}
                          onSort={handleSort}
                        >
                          {t('drivers:table.license')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="status"
                          sort={sort}
                          onSort={handleSort}
                        >
                          {t('drivers:table.status')}
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="vehicle"
                          sort={sort}
                          onSort={handleSort}
                        >
                          {t('drivers:table.vehicle')}
                        </SortableTableHead>
                        <TableHead>
                          {t('drivers:table.certifications')}
                        </TableHead>
                        <TableHead>{t('drivers:table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedDrivers.map((driver) => (
                        <DriverTableRow
                          key={driver.id}
                          driver={driver as any}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <ListPagination
                page={data.page}
                totalPages={data.totalPages}
                pageSize={data.pageSize}
                totalCount={data.totalCount}
                hasPrevious={data.hasPrevious}
                hasNext={data.hasNext}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                className="mt-4 px-4 max-md:px-0"
              />
            </>
          ) : (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {t('drivers:noDriversFound')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
